/* eslint-disable @typescript-eslint/no-empty-object-type */
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/_shadcn-ui/tooltip';
import Pagination from '@/components/ui/pagination';
import PaginationLimit from '@/components/ui/pagination-limit';
import { displayRole } from '@/features/user/utils/check-role';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { useServerPagination } from '@/hooks/use-server-pagination';
import { Mail, SquareX } from 'lucide-react';
import { memo, useCallback, useMemo } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '@/components/_ui/modal';
import {
  GET_LIST_TICKETING_USER_INVITATION_QUERY_KEY,
  useListTicketingUserInvitationQuery,
} from '../../api/get-list-invitation-ticketing-user.api';
import { useResendInviteAsAdminTicketingUserMutation } from '../../api/resend-invite-as-admin-ticketing-user.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { TTicketingUserInvitation } from '../../types/ticketing-user.type';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';

const TOAST_MESSAGES_INVITATION_MODAL = {
  RESEND_INVITATION_ERROR: 'Error resending invitation, please try again',
  RESEND_INVITATION_SUCCESS: 'The invitation has been successfully resent',
  DELETE_INVITATION_ERROR: 'Error deleting invitation, please try again',
  DELETE_INVITATION_SUCCESS: 'The invitation has been successfully deleted',
};

export type TInvitationTicketingUserModalType = 'InvitationTicketingUserModal';
export interface IInvitationTicketingUserModalProps {}
const InvitationTicketingUserModal = memo((_props: IInvitationTicketingUserModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();
  const { currentPage, itemsPerPage, handlePageChange, handleItemsPerPageChange, apiParams } = useServerPagination({
    initialItemsPerPage: 10,
    initialPage: 1,
  });

  const isOpenInvitationTicketingUserModal = useMemo(() => {
    return modalActions.checkIsOpenModal('InvitationTicketingUserModal');
  }, [modalActions]);

  const { data: ticketingUserInvitationData, isLoading: isLoadingTicketingUserInvitation } =
    useListTicketingUserInvitationQuery({
      params: {
        ...apiParams,
        sortDir: 'ASC',
        sortBy: 'fullname',
      },
      queryConfig: {
        enabled: !!isOpenInvitationTicketingUserModal,
      },
    });

  const currentData = ticketingUserInvitationData?.payload?.rows || [];
  const totalItems = ticketingUserInvitationData?.payload?.count || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const mutationResendInvitation = useResendInviteAsAdminTicketingUserMutation({
    mutationConfig: {
      onError: (error) => {
        const msgError = axiosExtractErrorMessage(error) || TOAST_MESSAGES_INVITATION_MODAL.RESEND_INVITATION_ERROR;
        showToast({
          type: 'error',
          message: msgError,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_LIST_TICKETING_USER_INVITATION_QUERY_KEY] });
        showToast({
          type: 'success',
          message: TOAST_MESSAGES_INVITATION_MODAL.RESEND_INVITATION_SUCCESS,
        });
      },
    },
  });

  // HANDLERS
  const handleResendInvitation = useCallback(
    (invitation: TTicketingUserInvitation) => {
      mutationResendInvitation.mutate({ invitationId: invitation.invitationId });
    },
    [mutationResendInvitation]
  );

  const handleDeleteInvitation = useCallback(
    (invitation: TTicketingUserInvitation) => {
      modalActions.openModal('DeleteInvitationTicketingUserModal', { invitationId: invitation.invitationId });
    },
    [modalActions]
  );

  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('InvitationTicketingUserModal');
  }, [modalActions]);

  const handleClose = useCallback(() => {
    handleCloseModal();
  }, [handleCloseModal]);

  // RENDER
  return (
    <Modal
      isOpen={isOpenInvitationTicketingUserModal}
      lockBodyScroll
      onClose={handleClose}
      className="p-[32px] md:w-[1000px] w-[345px]"
    >
      <ModalHeader>
        <ModalTitle>Pending Invitation</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="w-full">
          {isLoadingTicketingUserInvitation ? (
            <PendingInvitationTableSkeleton />
          ) : (
            <>
              <div className="rounded-[8px] rounded-b-none overflow-hidden border border-[#D9D9D9]">
                <div className="w-full overflow-x-auto">
                  <table
                    className="min-w-[760px] w-full text-left rtl:text-right"
                    role="table"
                    aria-label="Pending invitation ticketing table"
                  >
                    <thead className="text-[14px] font-normal bg-[#0C8DBF] text-white">
                      <tr role="row">
                        <th className="w-[60px] text-center py-3" scope="col" role="columnheader">
                          No
                        </th>
                        <th className="w-[200px] px-6 py-3" scope="col" role="columnheader">
                          Name
                        </th>
                        <th className="w-[200px] px-6 py-3" scope="col" role="columnheader">
                          Email
                        </th>
                        <th className="w-[150px] px-6 py-3" scope="col" role="columnheader">
                          Role
                        </th>
                        <th className="w-[150px] px-6 py-3 text-center" scope="col" role="columnheader">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentData.map((invitation, index: number) => (
                        <tr
                          key={invitation.invitationId}
                          className={`bg-white ${index !== currentData.length - 1 ? 'border-b border-[#D9D9D9]' : ''}`}
                          role="row"
                        >
                          <td
                            className="text-center w-[60px] text-[#25151E] font-normal whitespace-normal break-words text-xs sm:text-sm"
                            role="cell"
                          >
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td
                            className="w-[200px] px-6 py-3 text-xs sm:text-sm text-[#25151E] font-normal whitespace-normal break-words"
                            role="cell"
                          >
                            {invitation.fullname}
                          </td>
                          <td
                            className="w-[200px] px-6 py-3 text-xs sm:text-sm text-[#25151E] font-normal whitespace-normal break-words"
                            role="cell"
                          >
                            {invitation.email}
                          </td>
                          <td
                            className="w-[150px] px-6 py-3 text-xs sm:text-sm text-[#25151E] font-normal whitespace-normal break-words"
                            role="cell"
                          >
                            {displayRole(invitation.role?.name)}
                          </td>
                          <td className="w-[150px] px-6 py-3" role="cell">
                            <div className="flex justify-center items-center gap-[10px]">
                              <Tooltip>
                                <TooltipTrigger asChild className="cursor-pointer">
                                  <button
                                    type="button"
                                    className="size-6 p-1 bg-[#F5F5F5] rounded-sm justify-center items-center flex"
                                    onClick={() => handleResendInvitation(invitation)}
                                    title="Resend Invitation"
                                    aria-label={`Resend invitation to ${invitation.email}`}
                                  >
                                    <Mail className="text-[#0C8DBF] size-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="bg-white text-[#25151E] border max-w-[300px] border-[#D9D9D9] font-normal text-[14px]  z-1000">
                                  <p>Resend Invitation</p>
                                </TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild className="cursor-pointer">
                                  <button
                                    type="button"
                                    className="size-6 p-1 bg-[#F5F5F5] rounded-sm justify-center items-center flex"
                                    onClick={() => handleDeleteInvitation(invitation)}
                                    title="Delete Invitation"
                                    aria-label={`Delete invitation for ${invitation.email}`}
                                  >
                                    <SquareX className="text-[#05223E] size-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="bg-white text-[#25151E] border max-w-[300px] border-[#D9D9D9] font-normal text-[14px]  z-1000">
                                  <p>Delete Invitation</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="flex flex-row justify-between items-center gap-0 py-3 px-4 sm:space-x-4 bg-white border border-t-0 border-[#D9D9D9] rounded-b-[8px]">
                <div className="text-xs md:text-sm font-normal text-[#25151E]">Total {totalItems} entries</div>
                <div className="flex flex-row gap-2 md:space-x-2">
                  <PaginationLimit value={itemsPerPage} onChange={handleItemsPerPageChange} />
                  <Pagination
                    limit={itemsPerPage}
                    page={currentPage}
                    count={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
});
InvitationTicketingUserModal.displayName = 'InvitationTicketingUserModal';
export default InvitationTicketingUserModal;

// ------------------------------------------------------------------------
// Pending Invitation Table Skeleton
const PendingInvitationTableSkeleton = memo(() => {
  return (
    <div>
      <div className="rounded-[8px] rounded-b-none overflow-hidden border border-[#D9D9D9]">
        <div className="w-full overflow-x-auto">
          <table className="min-w-[760px] w-full text-left rtl:text-right">
            <thead className="text-[14px] font-normal bg-[#0C8DBF] text-white">
              <tr>
                <th className="w-[60px] text-center py-3">No</th>
                <th className="w-[200px] px-6 py-3">Name</th>
                <th className="w-[200px] px-6 py-3">Email</th>
                <th className="w-[150px] px-6 py-3">Role</th>
                <th className="w-[150px] px-6 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, index) => (
                <tr key={index} className={`bg-white ${index !== 4 ? 'border-b border-[#D9D9D9]' : ''}`}>
                  <td className="text-center w-[60px] text-[#25151E] font-normal whitespace-normal break-words">
                    <div className="h-4 w-8 bg-gray-200 rounded animate-pulse mx-auto" />
                  </td>
                  <td className="w-[200px] px-6 py-3">
                    <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="w-[200px] px-6 py-3">
                    <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="w-[150px] px-6 py-3">
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="w-[150px] px-6 py-3">
                    <div className="flex justify-center items-center gap-[10px]">
                      <div className="size-6 bg-gray-200 rounded-sm animate-pulse" />
                      <div className="size-6 bg-gray-200 rounded-sm animate-pulse" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Skeleton */}
      <div className="flex flex-row justify-between items-center gap-0 py-3 px-4 sm:space-x-4 bg-white border border-t-0 border-[#D9D9D9] rounded-b-[8px]">
        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="flex flex-row gap-2 md:space-x-2">
          <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
});
PendingInvitationTableSkeleton.displayName = 'PendingInvitationTableSkeleton';
